<template>
  <div :id="manuallyExpanded ? 'manually-expanded' : ''"
       class="ai-passage-retrieval-container"
       :class="{'will-overflow': isComponentOverflowed && aiSearchRequestReturned, 
                'no-overflow': !isComponentOverflowed && aiSearchRequestReturned}">
    <div class="ai-passage-retrieval-header">
      <div class="header-text" role="heading" aria-level="2">
        <span> {{ C_AI_SEARCH.AI_COMPONENT_TEXT.HEADER }} </span>
        <span> BETA </span>
        <a class="learn-more wkce-icon-info" />
      </div>     
      <div v-if="shouldShowResults" id="desktop-feedback" class="feedback-bubble">
        <span> {{ C_AI_SEARCH.AI_COMPONENT_TEXT.FEEDBACK }} </span>
        <ai-feedback survey-path="/jfe/form/SV_cLVtzIv6BGHXgX4"
                     :survey-meta="getSurveyMeta" />
      </div>
    </div>
    <div v-if="shouldShowSpinner" class="loading-content">
      <div class="wkce-icon-spinner wk-spin" />
    </div>
    <div v-else-if="shouldShowDelayMessage"
         class="timeout-container loading-content">
      <div class="wkce-icon-spinner wk-spin" />
      <span v-for="(msg, i) in getErrorMessage('TIMEOUT')" :key="msg + '-timeout-' + i">
        {{ msg }}
      </span>
    </div>
    <div v-else-if="shouldShowResults" class="ai-results">
      <span class="sub-header"> {{ C_AI_SEARCH.AI_COMPONENT_TEXT.SUB_HEADER }} </span>
      <ai-results-container :search-results="searchResults" />
      <div v-if="shouldShowResults" id="mobile-web-feedback" class="feedback-bubble">
        <span> {{ C_AI_SEARCH.AI_COMPONENT_TEXT.FEEDBACK }} </span>
        <ai-feedback survey-path="/jfe/form/SV_cLVtzIv6BGHXgX4"
                     :survey-meta="getSurveyMeta" />
      </div>
    </div>
    <div v-else-if="shouldShowError"
         class="error-container"
         :class="checkErrorType">
      <span v-for="(msg, i) in getErrorMessage('GENERIC')" :key="msg + '-generic-' + i">
        {{ msg }}
      </span>
    </div>
    <div class="content-visibility-buttons">
      <button class="show-more-button" 
              @click="showMore()">
        Show More Results
      </button>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { TopicLinkTypes } from '_acaSrc/utility/contents/topic/topic';
import { throttleBounce } from '_acaSrc/utility/timers';
import { getWindow, decodeHtml } from '_acaSrc/utility/DOM';
import { SET_AI_COMPONENT_HAS_BEEN_SEEN, 
    SET_AI_PANEL_EXPAND_ON_RETURN } from '_acaSrc/store/ai.store';
import { SET_REFRESH_FOR_AI_RESULTS } from '_acaSrc/store/graphic.store';
import {
    setSearchUrlParamsHelper,
    makeRelativeUrl,
    getLeftmostSubdomain
} from '_acaSrc/utility/http';
import AiFeedback from '_acaSrc/components/contents/search/aiSearch/AiFeedback.vue';
import AiResultsContainer from '_acaSrc/components/contents/search/aiSearch/AiResultsContainer.vue';
import { C_AI_SEARCH } from '_acaSrc/utility/constants';
import { logUiClickEventHelper } from '_acaSrc/utility/Events';

const inlineLinksSelector = '.snippet-container a';
const inlineGfxSelector = '.snippet-container a.graphic';

export default {
    components: { 
        AiFeedback,
        AiResultsContainer
    },
    props: {
        searchResults: {
            type: Array
        },
        error: {
            type: Object,
            default: () => {
                return { hasError: false };
            }
        }
    },
    data() {
        return {
            manuallyExpanded: false,
            componentHeights: {
                scrollHeight: null,
                clientHeight: null
            },
            hasTimedOut: false,
            C_AI_SEARCH
        };
    },
    computed: {
        ...mapGetters('search', [ 'searchFor' ]),
        ...mapGetters('ai', [ 'aiSearchRequestReturned', 
            'aiPanelExpandOnReturn', 'prevAiQueryData' ]),
        ...mapGetters('profile', [ 'userProfile' ]),
        isComponentOverflowed() {
            if (!this.componentHeights ||
                !this.componentHeights.scrollHeight ||
                !this.componentHeights.clientHeight) {
                return;
            }
            return this.componentHeights.scrollHeight -
                   this.componentHeights.clientHeight >=
                   C_AI_SEARCH.RESIZE_OVERFLOW_TOLERANCE_PX;
        },
        shouldShowSpinner() {
            return !this.aiSearchRequestReturned && !this.hasTimedOut;
        },
        shouldShowDelayMessage() {
            return !this.aiSearchRequestReturned && this.hasTimedOut;
        },
        shouldShowResults() {
            return this.aiSearchRequestReturned && !this.error.hasError;
        },
        shouldShowError() {
            return this.aiSearchRequestReturned && this.error.hasError;
        },
        checkErrorType() {
            switch (this.error && this.error.errorType) {
            case C_AI_SEARCH.ERROR_TYPES.QUERY_REJECTION:
                return 'query-rejection';
            case C_AI_SEARCH.ERROR_TYPES.NO_RESULTS:
                return 'no-ai-results';
            case C_AI_SEARCH.ERROR_TYPES.LANGUAGE_NOT_SUPPORTED:
                return 'language-not-supported';
            case C_AI_SEARCH.ERROR_TYPES.FILTERED_RESULTS:
                return 'all-results-filtered';
            default:
                return null;
            }
        },
        getSnippetsAndTopicSectionIds() {
            if (!this.searchResults) {
                return {};
            }

            const snippetArray = [];
            const topicSectionIds = [];

            this.searchResults.forEach(result => {
                const curTopicId = result.topicId || '';
                const curSection = result.sectionId || '';
                const snippet = result.snippet && decodeHtml(result.snippet);

                const snippetCutoffMax = C_AI_SEARCH.QUALTRICS.SNIPPET_MAX_BEFORE_TRUNC;
                const ellipsisLength = C_AI_SEARCH.QUALTRICS.ELLIPSIS_LENGTH;
                const truncatedMax = C_AI_SEARCH.QUALTRICS.SNIPPET_TRUNC_CUTOFF;

                if (snippet.length <= snippetCutoffMax + (ellipsisLength * 2)) {
                    snippetArray.push(
                        `${snippet.slice(ellipsisLength, -ellipsisLength)} - ${curTopicId}`
                    );
                }
                else {
                    // Snippet is truncated to first and last 20 characters sans ellipsis
                    const stringToAdd = 
                      `${snippet.slice(ellipsisLength, truncatedMax + ellipsisLength)
                      }...${snippet.slice(-truncatedMax - ellipsisLength, -ellipsisLength)
                      } - ${curTopicId}`;

                    snippetArray.push(stringToAdd);
                }
                topicSectionIds.push(`${curTopicId}#${curSection}`);
            });
            
            return {
                snippets: snippetArray.join('. '),
                topicSectionIds: topicSectionIds.join(', ')
            };
        },
        getSurveyMeta() {
            const userProfile = this.userProfile;
            const query = this.searchFor || '';
            const surveyMeta = { query };

            if (userProfile) {
                surveyMeta.utdId = userProfile?.visitor?.userUtdId || '';
                surveyMeta.environment = userProfile?.visitor?.environment || '';
                surveyMeta.accountId = userProfile?.account?.id || '';
            }

            if (getLeftmostSubdomain() === 'qa2www') {
                surveyMeta.environment = 'QA2';
            }
            
            const { snippets = '', topicSectionIds = '' } = this.getSnippetsAndTopicSectionIds;
            surveyMeta.snippets = snippets;
            surveyMeta.topicSectionIds = topicSectionIds;

            return surveyMeta;
        }
    },
    watch: {
        aiSearchRequestReturned: {
            handler() {
                this.$nextTick(() => {
                    this.processResults();
                });
            },
            deep: true
        }
    },
    mounted() {
        setTimeout(
            () => this.hasTimedOut = true, C_AI_SEARCH.MAX_TIMEOUT_MS
        );
        this[SET_REFRESH_FOR_AI_RESULTS](false);
        this[SET_AI_COMPONENT_HAS_BEEN_SEEN](true);

        const isSameQuery = this.prevAiQueryData.query === this.searchFor;
        if (this.aiPanelExpandOnReturn && isSameQuery) {
            this.manuallyExpanded = true;
        } 
        else {
            this[SET_AI_PANEL_EXPAND_ON_RETURN](false);
        }

        if (this.aiSearchRequestReturned) {
            this.resizeCheck();
            this.processResults();
        }
    },
    beforeUnmount() {
        Array.from(this.$el.querySelectorAll(inlineGfxSelector))
            .forEach(el => {
                el.removeEventListener('click', this.onClickInlineGraphic);
            });
    },
    methods: {
        ...mapActions('graphic', [ 'handleUseGraphicLink', 'updateGfxHoverTooltipBinding' ]),
        ...mapMutations('graphic', [ 'SET_REFRESH_FOR_AI_RESULTS' ]),
        ...mapMutations('ai', [ 'SET_AI_COMPONENT_HAS_BEEN_SEEN', 
            'SET_AI_PANEL_EXPAND_ON_RETURN' ]),
        setupIcons() {
            Array.from(this.$el.querySelectorAll(inlineLinksSelector))
                .forEach(link => {
                    if (TopicLinkTypes.isExternal(link)) {
                        link.classList.add('ds1-inline-svg-link', 'external-link');
                    }
                    else if (TopicLinkTypes.isPathway(link)) {
                        link.classList.add('ds1-inline-svg-link', 'related-pathway-link');
                    }
                });
        },
        async onClickInlineGraphic(evt) {
            await this.handleUseGraphicLink({ evt,
                options: {
                    isFromCollection: true,
                    skipSidebarStateReset: false
                } });
        },
        addLinkQps() {
            const snippets = this?.$el?.querySelectorAll('.snippet');
            if (!snippets) {
                return;
            }

            snippets.forEach((snippet) => {
                this.processLinks(snippet, 'a', {
                    usageType: C_AI_SEARCH.EVENTS.USAGE_TYPE,
                    sourceValue: C_AI_SEARCH.EVENTS.TOPIC_SOURCE,
                    searchTerm: this.searchFor || '',
                    rankIndex: snippet?.getAttribute('display-rank') || '',
                    searchRank: snippet?.getAttribute('search-rank') || ''
                });

                this.processLinks(snippet, 'a.graphic, a.abstract_t, a.grade, a.drug, a.calc', {
                    section: snippet?.getAttribute('section') || '',
                    sectionValue: snippet?.getAttribute('section-id') || ''
                });
            });
        },
        processResults() {
            this.$nextTick(() => {
                this.$nextTick(() => {
                    this.setupIcons();
                    this.addLinkQps();
                });
                Array.from(this.$el.querySelectorAll(inlineGfxSelector))
                    .forEach(el => {
                        el.addEventListener('click', this.onClickInlineGraphic);
                    });
                this.resizeCheck();
                this.resizeDebounce = throttleBounce(
                    () => this.resizeCheck(),
                    'ai-search-results-resize',
                    200
                );
                this.resizeListener = this.setResizeListener(getWindow(), this.resizeDebounce);
                this[SET_REFRESH_FOR_AI_RESULTS](true);
            });
        },
        resizeCheck() {
            const component = this.$el;
            if (!component || !component.scrollHeight || !component.clientHeight) {
                return;
            } 
            this.componentHeights.scrollHeight = component.scrollHeight;
            this.componentHeights.clientHeight = component.clientHeight;
        },
        getErrorMessage(type) {
            return C_AI_SEARCH.ERROR_TEXT[type];
        },
        showMore() {
            this.logUiClickEvent('NLP_SEARCH'); 
            this.manuallyExpanded = true;
            this[SET_AI_PANEL_EXPAND_ON_RETURN](true);
        },
        logUiClickEvent(element) {
            logUiClickEventHelper({
                uiElementName: element,
                optData: 'ShowMore',
                searchTerm: this.searchFor || ''
            });
        },
        processLinks(snippet, selector, params) {
            const links = snippet?.querySelectorAll(selector);
            if (!links) {
                return;
            }

            links.forEach((link) => {
                const newUrl = setSearchUrlParamsHelper(link.href, params);
                link.href = makeRelativeUrl(newUrl) || '';
            });
        }
    }
};
</script>

<style lang="less" scoped>
@import (reference) '~_acaAssets/wkce/colors/wkce-app-styles';

.ai-passage-retrieval-container {
  .ds1-pa-2();
  position: relative;
  height: 25vh;
  max-height: 318px;
  min-height: 60px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: @WKCE-BLUE-TINT5;
  border: solid @WKCE-GRAY-TINT4;
  border-width: 1px 0;

  .feedback-bubble {
    .ds1-pl-2();
    .ds1-pr-2();
    margin-left: auto;
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: @WKCE-GRAY;
    font-size: 12px;
    font-weight: 400;
    border-radius: 8px;
    box-sizing: border-box;

    &#mobile-web-feedback {
      > span {
        .ds1-mr-1();
        color: @WKCE-GRAY-SHADE1;
        font-weight: 400;
      }

      :deep(.thumb-controls > a) {
        border: 1px solid @WKCE-BLUE-TINT1;
        border-radius: 8px;
        background: @WKCE-WHITE;
      }
    }
  }
  
  #desktop-feedback {
    display: none;
  }

  .ai-results {
    display: flex;
    flex-direction: column;
  }

  .sub-header {
    .ds1-mt-compact();
    color: @WKCE-GRAY;
    font-size: 12px;
    line-height: 18px;

    .bold {
      font-weight: 600;
    }
  }
  
  .content-visibility-buttons {
    display: flex;
    justify-content: center;

    .show-more-button {
      width: 458px;
      height: 32px;
      position: absolute;
      bottom: 12px;
      left: 50%;
      z-index: 1;
      display: flex;
      justify-content: center;
      align-items: center;
      font-size: 14px;
      font-weight: 600;
      color: @WKCE-BLUE-SHADE1;
      background: @WKCE-WHITE;
      border: 1px solid @WKCE-BLUE-TINT3;
      border-radius: 10px;
      opacity: 0;
      transition: opacity 0.5s ease-in-out,
                  border-color 0.25s ease-in-out,
                  background-color 0.25s ease-in-out;
      transform: translate(-50%, 0);
      pointer-events: none;
    }
  }

  &::before {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width:100%;
    height:100%;
    z-index: 1;
    opacity: 0;
    transition: opacity 0.5s ease-in-out;
    pointer-events: none;
    /* stylelint-disable-next-line color-no-hex */
    background:linear-gradient(transparent calc(25vh - 40px), #DBEBF4);
  }

  &.will-overflow {
    &::before, .show-more-button {
      opacity: 1;
    }

    .show-more-button {
      pointer-events: unset;

      &:hover {
        background: @WKCE-BLUE-TINT3;
        color: @WKCE-BLUE-SHADE2;
      }
    }
  }

  &#manually-expanded {
    max-height: 100%;
    height: 100%;

    &::before, .show-more-button {
      opacity: 0;
      pointer-events: none;
    }

    .show-more-button {
      color: transparent;
    }
  }

  .error-container, .timeout-container {
    margin: auto;
    display: flex;
    flex-direction: column;

    > span {
      text-align: center;
    }
  }

  .timeout-container > span {
    .ds1-mt-2();
  }

  .loading-content {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 25vh;

    .wkce-icon-spinner {
      font-size: 75px;
      width: 75px;
      height: 75px;
      color: @WKCE-BLUE;
    }
  }

  .ai-passage-retrieval-header {
    display: flex;
    justify-content: flex-start;
    flex-wrap: wrap;
    min-height: 25.6px;
    max-width: 1868px;
    gap: 16px; // Not IE11 supported, minor cosmetic tweak

    .header-text {
      font-weight: 600;
      font-size: 16px;
      line-height: 24px;
      color: @WKCE-GRAY-SHADE1;

      .learn-more {
        .ds1-ml-1();
        width: 14px;
        height: 14px;
        vertical-align: -2px;
        color: @WKCE-GREEN-SHADE2;
        font-weight: 500;
        font-size: 14px;

        &:hover {
          text-decoration: none;
          cursor: pointer;
        }
      }
      
      &::before {
        .ds1-mr-1();
        content: "";
        background: url(~_acaAssets/search/ai-icon.svg);
        background-size: 22px 22px;
        width: 22px;
        height: 22px;
        display: inline-block;
        vertical-align: top;
      }

      > span:nth-child(2) {
        .ds1-ml-compact();
        color: @WKCE-GREEN-SHADE2;
        font-size: 11px;
      }
    }
  }
}

@media only screen and (min-width: 768px) { 
  .isDesktop .ai-passage-retrieval-container {
    .ds1-mb-2();
    padding: 16px 26px;

    #mobile-web-feedback {
      display: none;
    }

    #desktop-feedback {
      display: flex;
    }

    .ai-passage-retrieval-header .feedback-bubble {
      height: 24px;
      width: 258px;
      margin-top: 0;
      margin-left: 0;
      border: 1px solid @WKCE-BLUE-TINT1;
      border-width: 1px 0;
      background: @WKCE-WHITE;
      border-radius: 6px;
      box-shadow: 0 4px 4px #00000014;

      :deep(.thumb-controls > a) {
        transform: scale(-1, 1);

        &.thumbs-up {
          margin-left: 0.5px;
          margin-right: 2.5px;
        }

        &.thumbs-down {
          margin-left: 2.5px;
          margin-right: 0.5px;
        }
      }
    }

    .sub-header {
      .ds1-mt-1();
      margin-left: 30px;
    }
  }
}
</style>